/**
 * Utilidades de formato de fechas para jornadas.
 *
 * Convención: jornada.fecha es siempre el SÁBADO.
 * Los partidos del domingo llevan partido.fecha con su fecha propia.
 * Para evitar confusión entre sedes, las fechas de jornada se muestran
 * siempre como rango: "sábado - domingo".
 */

/**
 * Parsea una fecha DATEONLY de PostgreSQL ("YYYY-MM-DD" o Date)
 * como fecha LOCAL para evitar el desfase de zona horaria UTC.
 */
const parseFechaLocal = (fecha) => {
  if (!fecha) return null;
  if (fecha instanceof Date) return fecha;
  const str = String(fecha).split('T')[0]; // "2026-10-03"
  const [y, m, d] = str.split('-').map(Number);
  return new Date(y, m - 1, d);
};

/**
 * Formato largo con año: "03/10/2026 - 04/10/2026"
 * @param {string|Date} fecha - Fecha del sábado (jornada.fecha)
 */
export const formatFechaJornada = (fecha) => {
  const sabado = parseFechaLocal(fecha);
  if (!sabado) return '—';
  const domingo = new Date(sabado);
  domingo.setDate(sabado.getDate() + 1);
  const fmt = { day: '2-digit', month: '2-digit', year: 'numeric' };
  return `${sabado.toLocaleDateString('es-ES', fmt)} - ${domingo.toLocaleDateString('es-ES', fmt)}`;
};

/**
 * Formato corto sin año: "03/10 - 04/10"
 * Útil en vistas compactas (chips, tablas).
 * @param {string|Date} fecha
 */
export const formatFechaJornadaCorta = (fecha) => {
  const sabado = parseFechaLocal(fecha);
  if (!sabado) return '—';
  const domingo = new Date(sabado);
  domingo.setDate(sabado.getDate() + 1);
  const fmt = { day: '2-digit', month: '2-digit' };
  return `${sabado.toLocaleDateString('es-ES', fmt)} - ${domingo.toLocaleDateString('es-ES', fmt)}`;
};

/**
 * Formato con día de la semana largo: "Sáb 03/10 - Dom 04/10"
 * @param {string|Date} fecha
 */
export const formatFechaJornadaConDia = (fecha) => {
  const sabado = parseFechaLocal(fecha);
  if (!sabado) return '—';
  const domingo = new Date(sabado);
  domingo.setDate(sabado.getDate() + 1);
  const fmtDia = { weekday: 'short' };
  const fmtFecha = { day: 'numeric', month: 'short' };
  const locale = 'es-ES';
  return `${sabado.toLocaleDateString(locale, fmtDia)} ${sabado.toLocaleDateString(locale, fmtFecha)} - ${domingo.toLocaleDateString(locale, fmtDia)} ${domingo.toLocaleDateString(locale, fmtFecha)}`;
};

/**
 * Formato de fecha simple (un solo día): "01/10/2026"
 * Usar para fechas que NO son jornadas (p.ej. fecha_limite_inscripcion),
 * donde mostrar un rango sáb-dom sería incorrecto.
 *
 * @param {string|Date} fecha
 * @param {boolean} [conHora=false] - Si true, añade "23:59" al final
 */
export const formatFechaSimple = (fecha, conHora = false) => {
  const d = parseFechaLocal(fecha);
  if (!d) return '—';
  const str = d.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
  return conHora ? `${str} 23:59` : str;
};

/**
 * Calcula la fecha límite de inscripción para una jornada.
 * Regla: siempre el jueves (2 días antes del sábado) a las 23:59.
 *
 * @param {string|Date} fechaJornada - jornada.fecha (el sábado)
 * @returns {string}  Ej: "01/10/2026 23:59"
 */
export const formatFechaLimiteInscripcion = (fechaJornada) => {
  const sabado = parseFechaLocal(fechaJornada);
  if (!sabado) return '—';
  const jueves = new Date(sabado);
  jueves.setDate(sabado.getDate() - 2);
  return formatFechaSimple(jueves, true); // "DD/MM/YYYY 23:59"
};

/**
 * Formatea la fecha de un partido individual.
 *
 * - Si `partidoFecha` está definida (partido.fecha != null): muestra esa fecha exacta
 *   con el día de la semana. Ej: "domingo 04/10/2026"
 * - Si no (NULL, hereda de jornada): muestra el rango sáb-dom.
 *   Ej: "03/10/2026 - 04/10/2026"
 *
 * @param {string|Date|null} partidoFecha  - partido.fecha (puede ser null)
 * @param {string|Date}      jornadaFecha  - jornada.fecha (siempre el sábado)
 * @param {'largo'|'corto'}  formato       - 'largo' con año (default), 'corto' sin año
 */
export const formatFechaPartido = (partidoFecha, jornadaFecha, formato = 'largo') => {
  if (partidoFecha) {
    const fecha = parseFechaLocal(partidoFecha);
    if (!fecha) return '—';
    if (formato === 'corto') {
      return fecha.toLocaleDateString('es-ES', { weekday: 'short', day: '2-digit', month: '2-digit' });
    }
    return fecha.toLocaleDateString('es-ES', { weekday: 'long', day: '2-digit', month: '2-digit', year: 'numeric' });
  }
  // Sin fecha concreta → rango de la jornada
  return formato === 'corto' ? formatFechaJornadaCorta(jornadaFecha) : formatFechaJornada(jornadaFecha);
};
